<template>
    <div class="min-h-screen">
        <LoaderWithLogo v-if="initializing || loadingUser" />
        <template v-if="!initializing && !loadingUser">
            <div class="grid-container">
                <TopBar v-if="user" class="header" />
                <Sidebar v-if="user" class="sidebar" />
                <div
                    class="main bg-light overflow-auto"
                    :class="{
                        'h-[calc(100svh-63px)]': user,
                    }"
                >
                    <router-view />
                </div>
            </div>
            <Loading />
            <Dialog />
            <Toaster class="pointer-events-auto" position="bottom-left" rich-colors close-button />
            <DialogsWrapper />
            <TicketForm />
            <AddContactModal />
            <LogDetailsSheet />
            <VueQueryDevtools
                v-if="
                    user?.roles?.find((role) => role.name === 'developer') !== undefined ||
                    env === 'development'
                "
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import Dialog from '@/components/Dialog.vue'
import Loading from '@/components/Loading.vue'
import { Toaster } from '@/components/ui/sonner'
import VueQueryDevtools from './VueQueryDevtools.vue'
import { defineAsyncComponent, onMounted, reactive, ref, watch } from 'vue'
import { DialogsWrapper } from 'vuejs-confirm-dialog'
import TopBar from './components/Topbar.vue'
import Sidebar from './components/sidebar/Sidebar.vue'
import Pusher from 'pusher-js'
import Echo from 'laravel-echo'
import httpClient from '@/utils/httpClient'
import { useUserQuery } from '@/queries/user'
import { useSecureQueryPersistence } from '@/composables/secure-query'
import LoaderWithLogo from '@/components/ui/loader/LoaderWithLogo.vue'
import { QueryObserver, useQueryClient } from '@tanstack/vue-query'
import { queryKeys } from '@/lib/query-keys/v1'

const AddContactModal = defineAsyncComponent(
    () => import('@/components/ui/table/company-contact/sheets/CreateCompanyContactSheet.vue')
)
const LogDetailsSheet = defineAsyncComponent(
    () => import('@/components/ui/sheet/logs/LogDetailsSheet.vue')
)
const TicketForm = defineAsyncComponent(() => import('@/modules/ticket/CreateForm.vue'))

const userQueryProps = reactive({
    enabled: false,
})
const { data: user, refetch, isFetching, error } = useUserQuery(userQueryProps)
const { initializePersistence, initializing, isInitialized } = useSecureQueryPersistence()
const env = ref(import.meta.env.MODE)
const queryClient = useQueryClient()
const observer = new QueryObserver(queryClient, {
    queryKey: queryKeys.user.key(),
})
const loadingUser = ref(false)
const snackbar = ref(null)
const loading = ref(null)
const confirm = ref(null)

observer.subscribe((result) => {
    loadingUser.value = result.isLoading
})

watch(
    () => isInitialized.value,
    (isInitialized) => {
        if (isInitialized) {
            userQueryProps.enabled = true
        }
    }
)

watch(isFetching, (fetching) => {
    if (!fetching && !user.value && !error.value) {
        refetch()
    }
})

onMounted(async () => {
    await initializePersistence()

    window.Pusher = Pusher

    window.Echo = new Echo({
        broadcaster: 'reverb',
        key: import.meta.env.VITE_REVERB_APP_KEY,
        wsHost: import.meta.env.VITE_REVERB_HOST,
        wsPort: import.meta.env.VITE_REVERB_PORT,
        wssPort: import.meta.env.VITE_REVERB_PORT,
        forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
        enabledTransports: ['ws', 'wss'],
        channelAuthorization: {
            customHandler: async (channel, callback) => {
                const { data, error } = await httpClient.post('broadcasting/auth', {
                    channel_name: channel.channelName,
                    socket_id: channel.socketId,
                })

                callback(error, data)
            },
        },
    })
})
</script>

<style scoped>
.grid-container {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-areas:
        'header header'
        'sidebar main';
}
.header {
    grid-area: header;
}
.sidebar {
    grid-area: sidebar;
}
.main {
    grid-area: main;
}
</style>
